// tslint:disable-next-line:max-line-length
import { Component, Input, forwardRef, AfterContentInit, ChangeDetectorRef, ChangeDetectionStrategy, Optional, ViewChild, Output, EventEmitter, Renderer, ElementRef } from '@angular/core';
import { RangeOptions, RangeSettings } from './range-slider.interface';
import { NG_VALUE_ACCESSOR, FormBuilder, ControlContainer, FormGroup, Validators, ControlValueAccessor, FormControl } from '@angular/forms';
import { NouisliderComponent } from 'ng2-nouislider';
import { InputmaskDirective } from '../../directives/inputmask/inputmask.directive';
import { WCCCustomEvents } from '../../helpers/wcc-custom-event';

@Component({
  selector: 'app-range-slider',
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
        multi: true,
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => RangeSliderComponent),
    }
  ]
})
export class RangeSliderComponent implements AfterContentInit, ControlValueAccessor {

    //#region @Inputs
    @Input()
    get settings(): RangeOptions {
        return this._options;
    }
    set settings(options: RangeOptions) {
        this._options = Object.assign(<RangeOptions> {
            hidden: false
        }, options);
        if (this.isValidValue(this.slider) && this.rangeFormGroup) {
            if (this.isValidValue(this.slider.slider)) {
                this.slider.slider.updateOptions(this.nuiSliderConfig, true);
            }
        }
    }
    @Input()
    formControlName: string;
    @Input()
    hidden: boolean;
    //#endregion

    @Output()
    change: EventEmitter<number[]> = new EventEmitter<number[]>();

    //#region ViewChilds
    @ViewChild(NouisliderComponent)
    slider: NouisliderComponent;

    @ViewChild('minRangeInputMask')
    minRangeInputMask: InputmaskDirective;

    @ViewChild('maxRangeInputMask')
    maxRangeInputMask: InputmaskDirective;

    @ViewChild('controlElement')
    controlElement: ElementRef;
    //#endregion

    //#region Private Variables
    private _options: RangeOptions;
    private _currentRange: number[];
    private _mainControl: FormControl;
    private _mainFormGroup: FormGroup;
    //#endregion

    //#region Public Variables
    rangeFormGroup: FormGroup;
    //#endregion

    //#region Propagations
    propagateTouch = (_: any) => { };
    propagateChange = (_: any) => { };
    //#endregion

    //#region Getters and Setters
    get minRangeOption (): RangeSettings {
        const option: RangeSettings = {
            value: 0,
            display: 'Min',
            masking: null
        };

        if (this.configurations) {
            if (this.isValidValue(this.configurations.minConfig)) {
                Object.assign(option, this.configurations.minConfig);
            }
        }
        return option;
    }

    get maxRangeOption (): RangeSettings {
        const option: RangeSettings = {
            value: 100,
            display: 'Max',
            masking: null
        };

        if (this.configurations) {
            if (this.isValidValue(this.configurations.maxConfig)) {
                Object.assign(option, this.configurations.maxConfig);
            }
        }
        return option;
    }

    get configurations (): RangeOptions {
        if (this.isValidValue(this.settings)) {
            return this.settings;
        }
        return {};
    }

    get nuiSliderConfig () {
        let start = [this.minRangeOption.value, this.maxRangeOption.value];

        if (this.isValidValue(this.rangeFormGroup)) {
            const value = this.rangeFormGroup.get('rangeValue').value;
            if (Array.isArray(value)) {
                start = value;
            }
        }

        return Object.assign({
            start,
            step: 1,
            connect: true,
            range: {
                'min': this.minRangeOption.value,
                'max': this.maxRangeOption.value
            },
            format: {
                // 'to' the formatted value. Receives a number.
                to: function (value) {
                    return value;
                },
                // 'from' the formatted value.
                // Receives a string, should return a number.
                from: function (value) {
                    return value;
                }
            }
        }, this.configurations.nuiConfig);
    }

    get currentValue (): number[] {
        return [this.rangeFormGroup.get('minValue').value, this.rangeFormGroup.get('maxValue').value];
    }

    get isValidConfiguration (): boolean {
        return this.validateConfig(this.configurations);
    }
    //#endregion

    //#region Angular Methods
    constructor(
        private fb: FormBuilder,
        private renderer: Renderer,
        private cd: ChangeDetectorRef,
        @Optional() private container: ControlContainer
    ) {
        this.initForm();
    }

    ngAfterContentInit(): void {
        this.initializeMainControl();
    }

    writeValue(range: string[] | string): void {
        if (this.rangeFormGroup && range !== undefined) {
            if (range === '') {
                range = [
                    this.settings.minConfig.value.toString(),
                    this.settings.maxConfig.value.toString()
                ];
            }
            const maxValue = this.validateMaxValue(parseFloat(range[1]), parseFloat(range[0]));
            const minValue = this.validateMinValue(parseFloat(range[0]), parseFloat(range[1]));

            this.setRangeDisplayValue(minValue.toString(), maxValue.toString());

            range = [minValue.toString(), maxValue.toString()];
            this.rangeFormGroup.get('rangeValue').setValue(range);
            this.cd.detectChanges();
        }
        this.propagateChange(range);
    }

    registerOnChange(fn: any): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.propagateTouch = fn;
    }
    //#endregion

    //#region Events
    onSlide (range) {
        const newRange = range.map(this.sanitizeValue);
        this.setRangeDisplayValue(newRange[0], newRange[1], true);
        this.cd.detectChanges();
    }

    onSliderChange (range): void {
        if (this.isValidValue(this._mainControl)) {
            if (this.rangeFormGroup.valid) {
                const newRange = range.map(this.sanitizeValue);
                const event = new WCCCustomEvents('change', {bubbles: true});
                this._mainControl.setValue(newRange);
                this.renderer.invokeElementMethod(this.controlElement.nativeElement, 'dispatchEvent', [event]);
            }
        }
    }

    onFilterValueChanged (event): void {
        const range = [
            parseFloat(this.minRangeInputMask.cleanValue()),
            parseFloat(this.maxRangeInputMask.cleanValue())
        ];
        this._mainControl.setValue(range);
    }
    //#endregion

    //#region Private Methods
    private setRangeDisplayValue(min: string, max: string, patchValue: boolean = false) {
        if (patchValue) {
            this.rangeFormGroup.get('minValue').patchValue(this.minRangeInputMask.maskedValue(min));
            this.rangeFormGroup.get('maxValue').patchValue(this.maxRangeInputMask.maskedValue(max));
        } else {
            this.rangeFormGroup.get('minValue').setValue(this.minRangeInputMask.maskedValue(min));
            this.rangeFormGroup.get('maxValue').setValue(this.maxRangeInputMask.maskedValue(max));
        }
    }

    private sanitizeValue (val) {
        return val.toString().split('.')[0];
    }

    private validateMaxValue (val: number, minValue: number): number {
        const settings = this.settings;
        // verify from current config
        if (val < minValue && minValue >= settings.minConfig.value && minValue <= settings.maxConfig.value) {
            return minValue;
        }
        if (val > settings.maxConfig.value) {
            return settings.maxConfig.value;
        }
        return val;
    }

    private validateMinValue (val: number, maxValue: number): number {
        const settings = this.settings;
        // verify from current config
        if (val < settings.minConfig.value) {
            return settings.minConfig.value;
        }
        if (val > maxValue && maxValue >= settings.minConfig.value) {
            return maxValue;
        }
        return val;
    }

    private initForm () {
        this.rangeFormGroup = this.fb.group({
            minValue: new FormControl(this.minRangeOption.value, Validators.required),
            maxValue: new FormControl(this.maxRangeOption.value, Validators.required),
            rangeValue: [[this.minRangeOption.value, this.maxRangeOption.value]]
        });
    }

    private initializeMainControl () {
        const mainFormGroup = <FormGroup> this.container.control;
        const mainControl = <FormControl> mainFormGroup.controls[this.formControlName];
        if (mainControl === undefined || mainControl === null) {
            console.warn(`Warn:
            RnageSliderComponent::ngAfterContentInit sorry control name
                '` + this.formControlName + `' doesn\'t exist.`);
            return;
        }

        this._mainControl = mainControl;
        this._mainFormGroup = mainFormGroup;
    }

    private isValidValue (val: any): boolean {
        return val !== null && val !== undefined;
    }
    //#endregion

    //#region Public Methods
    validateConfig (config: RangeOptions) {
        return config.minConfig.value !== config.maxConfig.value;
    }
    //#endregion

}



// WEBPACK FOOTER //
// ./src/app/shared/components/range-slider/range-slider.component.ts